import { createContext, useContext } from "react";
import type { QuestionnaireSummaryCta, User } from "@ferrivia/api-contract";

export type SummarySource = "home" | "infusion_step" | "support_step" | "summary_list" | "submitted";

export interface OpenSummaryOptions {
  source: SummarySource;
  /** The tab the summary was opened from, when it has a booking path of its
      own. Overrides the summary's own cta. */
  path?: QuestionnaireSummaryCta;
}

export interface AppOutletContext {
  user: User;
  signOut: () => void;
  /** The app shell's own root node — what `#root` renders into, sized to the
      visible viewport rather than the window (see `AppShell.module.css`).
      Pass this as a `Sheet`'s `container` so it portals inside that box
      instead of `document.body`, which would size against the full window
      and sit under the keyboard. Null until the shell has mounted. */
  rootElement: HTMLElement | null;
  /** Opens the full-screen questionnaire overlay AppShell owns, so a CTA
      buried in a home block (ScreenerSheet) can trigger it without prop
      drilling through HomeScreen's RENDERERS map. */
  openQuestionnaire: () => void;
  /** Opens the full-screen questionnaire summary overlay AppShell owns. */
  openSummary: (id: string, options: OpenSummaryOptions) => void;
  /** Bumps each time the questionnaire or summary overlay closes. Surfaces
      that show what those overlays change (home's summary notice, the
      tabs' summary list) refetch on it, since every tab stays mounted. */
  overlayClosedCount: number;
}

export const AppContext = createContext<AppOutletContext | null>(null);

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used within AppShell");
  return context;
}
